import React from 'react';
import { alpha } from '@mui/material';
import { InputAdornment } from '@mui/material';

export const FORM_NEON = '#38BDF8';
export const FORM_SURFACE_BASE = '#050d1a';
export const FORM_SURFACE_MID = '#0a1628';
export const FORM_SURFACE_UP = '#0f1f38';
export const FORM_TEXT_BODY = '#E2E8F0';

/** Chip styling for MUI Autocomplete `slotProps.chip` (multi-select tags). */
export const autocompleteChipSx = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.8rem',
  height: 26,
  maxWidth: '100%',
  backgroundColor: `${alpha(FORM_NEON, 0.16)} !important`,
  border: `1px solid ${alpha(FORM_NEON, 0.35)}`,
  color: `${alpha(FORM_TEXT_BODY, 0.95)} !important`,
  '&.MuiChip-filled, &.MuiChip-filledDefault': {
    backgroundColor: `${alpha(FORM_NEON, 0.16)} !important`,
    color: `${alpha(FORM_TEXT_BODY, 0.95)} !important`,
  },
  '& .MuiChip-label': {
    color: `${alpha(FORM_TEXT_BODY, 0.95)} !important`,
    WebkitTextFillColor: `${alpha(FORM_TEXT_BODY, 0.95)} !important`,
  },
  '& .MuiChip-deleteIcon': {
    color: `${alpha(FORM_NEON, 0.75)} !important`,
    '&:hover': { color: `${FORM_NEON} !important` },
  },
};

/** Shared outlined field chrome for TextField, Select, Autocomplete, and pickers. */
export const inputRootSx = {
  '&.MuiFormControl-root': {
    alignItems: 'flex-start',
    gap: 0,
  },
  '& .MuiOutlinedInput-root': {
    flex: '0 0 auto',
    width: '100%',
    borderRadius: '10px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha(FORM_TEXT_BODY, 0.85),
    background: alpha(FORM_SURFACE_UP, 0.7),
    backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(FORM_NEON, 0.18), transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: alpha(FORM_NEON, 0.4) },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(FORM_NEON, 0.12)}, 0 0 14px ${alpha(FORM_NEON, 0.1)}`,
      animation: 'form-glow-in 0.3s ease',
    },
    '&.Mui-focused fieldset': { borderColor: alpha(FORM_NEON, 0.65), borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: 'rgba(248, 113, 113, 0.75) !important' },
    '&.Mui-error:hover fieldset': { borderColor: '#F87171 !important' },
    '&.Mui-error.Mui-focused fieldset': {
      borderColor: '#EF4444 !important',
      borderWidth: '1.5px',
      boxShadow: `0 0 0 3px ${alpha('#EF4444', 0.12)}`,
    },
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: '#F87171 !important',
  },
  '& .MuiInputBase-input': {
    color: `${alpha(FORM_TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(FORM_TEXT_BODY, 0.85)} !important`,
    '&::placeholder': { color: alpha(FORM_NEON, 0.3), opacity: 1 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.875rem',
    color: alpha(FORM_NEON, 0.45),
  },
  '& label.Mui-focused': { color: alpha(FORM_NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(FORM_NEON, 0.45), fontSize: 18 },
  '& .MuiSelect-icon': { color: alpha(FORM_NEON, 0.45) },
  '& .MuiFormHelperText-root': {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.68rem',
    letterSpacing: 0.3,
    color: `${alpha(FORM_TEXT_BODY, 0.78)} !important`,
    WebkitTextFillColor: `${alpha(FORM_TEXT_BODY, 0.78)} !important`,
    opacity: '1 !important',
    marginTop: 1,
    marginLeft: 0,
    marginRight: 0,
    lineHeight: 1.4,
    minHeight: 'unset',
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    color: `${alpha(FORM_NEON, 0.82)} !important`,
    WebkitTextFillColor: `${alpha(FORM_NEON, 0.82)} !important`,
    opacity: '1 !important',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#F87171 !important',
    WebkitTextFillColor: '#F87171 !important',
  },
  '& .MuiOutlinedInput-root .MuiSelect-select': {
    color: `${alpha(FORM_TEXT_BODY, 0.85)} !important`,
    WebkitTextFillColor: `${alpha(FORM_TEXT_BODY, 0.85)} !important`,
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: alpha(FORM_NEON, 0.45),
  },
  '& .MuiAutocomplete-inputRoot': {
    flex: '0 0 auto',
    flexWrap: 'wrap',
    gap: '4px',
    paddingTop: '8px !important',
    paddingBottom: '8px !important',
  },
  '& .MuiChip-root': {
    ...autocompleteChipSx,
    '& .MuiChip-label': {
      ...autocompleteChipSx['& .MuiChip-label'],
      paddingLeft: 8,
      paddingRight: 8,
    },
    '& .MuiChip-deleteIcon': {
      ...autocompleteChipSx['& .MuiChip-deleteIcon'],
      fontSize: 16,
    },
  },
};

const PICKER_DISABLED_BG = 'rgba(15, 23, 42, 0.60)';
const PICKER_DISABLED_BORDER = 'rgba(71, 85, 105, 0.55)';
const PICKER_DISABLED_HINT = 'rgba(148, 163, 184, 0.88)';

/** Extra rules for MUI X DatePicker / TimePicker text fields (v8 PickersOutlinedInput). */
export const pickerFieldSx = (size = 'normal', { disabled = false } = {}) => {
  const base = {
    flex: 1,
    width: '100%',
    ...inputRootSx,
    '& .MuiPickersOutlinedInput-root': {
      flex: '0 0 auto',
      width: '100%',
      borderRadius: '10px',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: size === 'small' ? '0.82rem' : '0.9rem',
      color: alpha(FORM_TEXT_BODY, 0.85),
      background: disabled ? PICKER_DISABLED_BG : alpha(FORM_SURFACE_UP, 0.7),
      backdropFilter: disabled ? 'none' : 'blur(8px)',
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease',
      cursor: disabled ? 'not-allowed' : 'pointer',
      '& fieldset': {
        borderColor: `${disabled ? PICKER_DISABLED_BORDER : alpha(FORM_NEON, 0.18)} !important`,
        borderWidth: '1px !important',
        transition: 'border-color 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: `${disabled ? PICKER_DISABLED_BORDER : alpha(FORM_NEON, 0.4)} !important`,
      },
      '&.Mui-focused': disabled
        ? {}
        : {
            boxShadow: `0 0 0 3px ${alpha(FORM_NEON, 0.12)}, 0 0 14px ${alpha(FORM_NEON, 0.1)}`,
            animation: 'form-glow-in 0.3s ease',
          },
      '&.Mui-focused fieldset': disabled
        ? { borderColor: `${PICKER_DISABLED_BORDER} !important` }
        : {
            borderColor: `${alpha(FORM_NEON, 0.65)} !important`,
            borderWidth: '1.5px !important',
          },
      '&.Mui-error fieldset': { borderColor: 'rgba(248, 113, 113, 0.75) !important' },
      '&.Mui-error:hover fieldset': { borderColor: '#F87171 !important' },
      '&.Mui-error.Mui-focused fieldset': {
        borderColor: '#EF4444 !important',
        borderWidth: '1.5px !important',
        boxShadow: `0 0 0 3px ${alpha('#EF4444', 0.12)}`,
      },
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: `${disabled ? PICKER_DISABLED_BORDER : alpha(FORM_NEON, 0.18)} !important`,
      transition: 'border-color 0.2s ease',
    },
    '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: `${disabled ? PICKER_DISABLED_BORDER : alpha(FORM_NEON, 0.4)} !important`,
    },
    '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: `${disabled ? PICKER_DISABLED_BORDER : alpha(FORM_NEON, 0.65)} !important`,
      borderWidth: disabled ? '1px !important' : '1.5px !important',
    },
    '& .MuiPickersSectionList-root': {
      fontFamily: "'DM Sans', sans-serif",
      fontSize: size === 'small' ? '0.82rem' : '0.9rem',
    },
    '& .MuiPickersSectionList-sectionContent, & .MuiPickersSectionList-separator': {
      color: disabled
        ? 'rgba(148, 163, 184, 0.5) !important'
        : `${alpha(FORM_TEXT_BODY, 0.85)} !important`,
      WebkitTextFillColor: disabled
        ? 'rgba(148, 163, 184, 0.5) !important'
        : `${alpha(FORM_TEXT_BODY, 0.85)} !important`,
    },
    '& .MuiInputLabel-root': {
      color: disabled ? 'rgba(148, 163, 184, 0.55) !important' : alpha(FORM_NEON, 0.45),
    },
    '& .MuiInputAdornment-positionStart .MuiSvgIcon-root': {
      color: disabled ? 'rgba(100, 116, 139, 0.55)' : alpha(FORM_NEON, 0.45),
    },
    '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
      color: disabled ? 'rgba(100, 116, 139, 0.45)' : alpha(FORM_NEON, 0.35),
      borderRadius: '8px',
      pointerEvents: disabled ? 'none' : 'auto',
      '&:hover': disabled ? {} : { color: FORM_NEON, background: alpha(FORM_NEON, 0.1) },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.68rem',
      letterSpacing: 0.3,
      color: disabled ? `${PICKER_DISABLED_HINT} !important` : alpha(FORM_NEON, 0.5),
      WebkitTextFillColor: disabled ? `${PICKER_DISABLED_HINT} !important` : undefined,
      opacity: '1 !important',
      marginTop: 1,
      marginLeft: 0,
      marginRight: 0,
      lineHeight: 1.4,
      minHeight: 'unset',
    },
    '& .Mui-disabled.MuiFormHelperText-root, & .MuiFormHelperText-root.Mui-disabled': {
      color: `${PICKER_DISABLED_HINT} !important`,
      WebkitTextFillColor: `${PICKER_DISABLED_HINT} !important`,
      opacity: '1 !important',
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: '#F87171 !important',
      WebkitTextFillColor: '#F87171 !important',
    },
  };

  return base;
};

/**
 * slotProps factory for MUI X DatePicker / TimePicker (matches MaintenanceForm).
 * @param {string} size - 'normal' | 'small'
 * @param {React.ReactElement|null} icon - start adornment icon
 * @returns {(disabled?: boolean) => object}
 */
export const makePickerSlots = (size = 'normal', icon = null) => (disabled = false) => ({
  textField: {
    fullWidth: true,
    size,
    InputLabelProps: { shrink: true },
    InputProps: icon
      ? {
          startAdornment: (
            <InputAdornment position="start">
              {React.cloneElement(icon, { sx: { fontSize: size === 'small' ? 16 : 18 } })}
            </InputAdornment>
          ),
        }
      : undefined,
    sx: pickerFieldSx(size, { disabled }),
  },
  popper: { placement: 'bottom-start' },
});

/**
 * Controlled open state + click-anywhere-on-field to open picker popup.
 */
export const buildPickerFieldProps = (
  pickerKey,
  pickerOpen,
  setPickerOpen,
  slots,
  { disabled = false, textField: textFieldExtra = {} } = {},
) => {
  const openPicker = () => {
    if (!disabled) setPickerOpen((prev) => ({ ...prev, [pickerKey]: true }));
  };
  const closePicker = () => setPickerOpen((prev) => ({ ...prev, [pickerKey]: false }));

  return {
    open: Boolean(pickerOpen[pickerKey]),
    onOpen: openPicker,
    onClose: closePicker,
    slotProps: {
      ...slots,
      textField: {
        ...slots.textField,
        ...textFieldExtra,
        onClick: openPicker,
        onKeyDown: (e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            openPicker();
          }
          textFieldExtra.onKeyDown?.(e);
        },
      },
      openPickerButton: {
        onClick: (e) => {
          e.stopPropagation();
          openPicker();
        },
      },
    },
  };
};
